import type * as THREE from 'three/webgpu';
import type { FeatureFace } from '../../mesh/feature';

/**
 * 求距用的几何基元种类。
 *
 * 设计说明：将测量选中体归一化为有限基元，供 {@link MeasureClosestPointResolver} 分派解析求距。
 */
export enum MeasureDistancePrimitiveKind {
    /** 点。 */
    Point = 'point',
    /** 有限线段。 */
    Segment = 'segment',
    /** 无限直线（基准轴等）。 */
    InfiniteLine = 'infiniteLine',
    /** 折线（边采样或圆弧折线近似）。 */
    Polyline = 'polyline',
    /** 无限延伸平面（基准面、默认参考面等）。 */
    Plane = 'plane',
    /** 有边界面域（模型拓扑面，含平面与解析曲面，通过三角网格投影求最近点）。 */
    MeshFace = 'meshFace',
}

/**
 * 点基元。
 */
export interface MeasureDistancePointPrimitive {
    /** 基元种类。 */
    kind: MeasureDistancePrimitiveKind.Point;
    /** 世界坐标。 */
    position: THREE.Vector3;
}

/**
 * 线段基元。
 */
export interface MeasureDistanceSegmentPrimitive {
    /** 基元种类。 */
    kind: MeasureDistancePrimitiveKind.Segment;
    /** 线段起点（世界坐标）。 */
    start: THREE.Vector3;
    /** 线段终点（世界坐标）。 */
    end: THREE.Vector3;
}

/**
 * 无限直线基元。
 */
export interface MeasureDistanceInfiniteLinePrimitive {
    /** 基元种类。 */
    kind: MeasureDistancePrimitiveKind.InfiniteLine;
    /** 直线上一点（世界坐标）。 */
    origin: THREE.Vector3;
    /** 单位方向。 */
    direction: THREE.Vector3;
}

/**
 * 折线基元。
 */
export interface MeasureDistancePolylinePrimitive {
    /** 基元种类。 */
    kind: MeasureDistancePrimitiveKind.Polyline;
    /** 世界坐标折线点列（至少 2 点）。 */
    points: readonly THREE.Vector3[];
}

/**
 * 无限平面基元。
 */
export interface MeasureDistancePlanePrimitive {
    /** 基元种类。 */
    kind: MeasureDistancePrimitiveKind.Plane;
    /** 平面上一点（世界坐标）。 */
    point: THREE.Vector3;
    /** 单位法向。 */
    normal: THREE.Vector3;
}

/**
 * 有边界网格面基元。
 */
export interface MeasureDistanceMeshFacePrimitive {
    /** 基元种类。 */
    kind: MeasureDistancePrimitiveKind.MeshFace;
    /** 面代理（通过三角网格在有边界面域内求最近点）。 */
    face: FeatureFace;
}

/**
 * 归一化后的求距几何体。
 */
export type MeasureDistanceGeometry =
    | MeasureDistancePointPrimitive
    | MeasureDistanceSegmentPrimitive
    | MeasureDistanceInfiniteLinePrimitive
    | MeasureDistancePolylinePrimitive
    | MeasureDistancePlanePrimitive
    | MeasureDistanceMeshFacePrimitive;

/**
 * 两几何体最近点求解结果。
 */
export interface MeasureClosestPointResult {
    /** 第一个选中实体上的最近点（世界坐标）。 */
    from: THREE.Vector3;
    /** 第二个选中实体上的最近点（世界坐标）。 */
    to: THREE.Vector3;
    /** 两点直线距离（毫米）。 */
    distance: number;
    /**
     * 是否展示双选测量 UI（测量线、双锚点、双选浮窗）。
     *
     * 零距离（两几何体相连或重合）时为 false，不发布双选浮窗状态。
     */
    shouldRenderMeasureLines: boolean;
}

/**
 * 垂直距离（法向距离）求解结果。
 *
 * 语义对齐 SolidWorks：点到直线/平面的无限延伸垂距；仅当与全局最短距离不等时展示。
 */
export interface MeasureNormalDistanceResult {
    /** 点侧端点（世界坐标）。 */
    from: THREE.Vector3;
    /** 无限直线/平面上的垂足（世界坐标，可在有限边/面外）。 */
    to: THREE.Vector3;
    /** 垂直距离值（毫米）。 */
    distance: number;
    /**
     * 是否应展示垂直距离（浮窗行 + 垂线）。
     *
     * 与 {@link MeasureClosestPointResult.distance} 相等时不展示。
     */
    shouldRender: boolean;
    /** 正交辅助线起点（有限体上的全局最近点）。 */
    transverseFrom: THREE.Vector3;
    /** 正交辅助线终点（垂足后再沿远离最近点方向延伸）。 */
    transverseTo: THREE.Vector3;
    /**
     * 是否应绘制正交辅助线（最近点 → 垂足 → 延伸段）。
     *
     * 仅当 {@link shouldRender} 为 true 且两端点不重合时为 true。
     */
    shouldRenderTransverse: boolean;
}
